import React, { useState, useEffect, useRef } from 'react';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import './OurPlans.css';
import InputBoxLarge from '../../../components/common/InputBox/InputBoxLarge';
import InputAndSelectCompoBoxSmall from '../../../components/common/InputBox/InputAndSelectCompo';
import FeaturesList from '../../../components/common/FeaturesListAdd/FeaturesListAdd';
import authenticatedAxios from '../../../authenticatedAxios';
import { baseurl } from '../../../api';
import { useNavigate } from 'react-router-dom';
import ToggleSwitch from '../../../components/common/ToggleButton/ToggleSwitch';
import InputBoxSmall from '../../../components/common/InputBox/InputBoxSmall';
import ErrorNotification from '../../../components/common/AlertBox/ErrorNotification';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';

// ─── Main component ──────────────────────────────────────────────────────────

const OurPlansAdd = () => {
    const [currencies, setCurrencies] = useState([]);
    const [features, setFeatures] = useState([]);
    const navigate = useNavigate();
    const [errors, setErrors] = useState({});



    const fetchCurrencies = async () => {
        try {
            const response = await authenticatedAxios.get(`${baseurl}currency/active/`);
            const options = (response.data.data || []).map(currency => ({
                label: currency.symbol,                        // shown in trigger after select
                searchLabel: `${currency.symbol} ${currency.name}`, // used for searching
                value: currency.id
            }));
            setCurrencies(options);
        } catch (error) {
            console.error('Error fetching currencies:', error);
        }
    };

    const fetchFeatures = async () => {
        try {
            const response = await authenticatedAxios.get(`${baseurl}plans/feature/`);
            if (response.data.response_code === 1) {
                const featureOptions = response.data.data.map(feature => ({
                    label: feature.name,
                    value: feature.id,
                }));
                setFeatures(featureOptions);
            } else {
                console.error("Error fetching features:", response.data.message);
            }
        } catch (error) {
            console.error("Error fetching features:", error);
        }
    };

    const [form, setForm] = useState({
        plan_name: '',
        display_name: '',
        monthly_price: '',
        yearly_price: '',
        currency: '',
        trial_period: '',
        badge_text: '',
        features: [],
        is_freeplan: false,
        is_advanced: false,

        default_users: '',
        default_maps_calls_monthly: '',
        default_maps_calls_yearly: '',
        default_ai_credits_monthly: '',
        default_ai_credits_yearly: '',
        base_maps_calls_count: '',
        base_ai_credits_count: '',
        add_user_price_monthly: '',
        add_user_price_yearly: '',
        base_maps_calls_price: '',
        base_ai_credits_price: '',
    });

    const handleChange = (key, value) => {
        // setErrors({});
        setForm(prev => {
            const updated = { ...prev, [key]: value };

            if (key === 'is_freeplan') {
                if (value === true) {
                    updated.is_advanced = false;
                } else {
                    updated.trial_period = ''; // ✅ clear when turned off
                }
            }

            if (key === 'is_advanced' && value === true) {
                updated.is_freeplan = false;
                updated.trial_period = ''; // ✅ also clear here
            }

            return updated;
        });
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[key];
            return newErrors;
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!form.plan_name?.trim()) {
            newErrors.plan_name = ["Plan Name is required"];
        }

        if (!form.display_name?.trim()) {
            newErrors.display_name = ["Display Name is required"];
        }

        if (!form.monthly_price) {
            newErrors.monthly_price = ["Monthly Price is required"];
        }

        if (!form.yearly_price) {
            newErrors.yearly_price = ["Yearly Price is required"];
        }

        if (!form.currency) {
            newErrors.currency = ["Currency is required"];
        }

        if (!form.features || form.features.length === 0) {
            newErrors.features = ["Features must not be empty"];
        }

        if (!form.default_users || Number(form.default_users) < 1) {
            newErrors.default_users = ["Default User Limit must be at least 1"];
        }

        return newErrors;
    };

    useEffect(() => {
        fetchCurrencies();
        fetchFeatures();
    }, []);

    const handleSubmit = async () => {

        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            const response = await authenticatedAxios.post(`${baseurl}plans/`, {
                ...form,
                monthly_price: parseFloat(form.monthly_price),
                yearly_price: parseFloat(form.yearly_price),

                // Only send trial_period when it's a free plan (model is non-nullable, so default to 0)
                trial_period: form.is_freeplan ? (parseInt(form.trial_period) || 0) : 0,

                default_users: parseInt(form.default_users) || 0,
                default_maps_calls_monthly: parseInt(form.default_maps_calls_monthly) || 0,
                default_maps_calls_yearly: parseInt(form.default_maps_calls_yearly) || 0,
                default_ai_credits_monthly: parseInt(form.default_ai_credits_monthly) || 0,
                default_ai_credits_yearly: parseInt(form.default_ai_credits_yearly) || 0,
                base_maps_calls_count: parseInt(form.base_maps_calls_count) || 0,
                base_ai_credits_count: parseInt(form.base_ai_credits_count) || 0,
                add_user_price_monthly: parseFloat(form.add_user_price_monthly) || 0,
                add_user_price_yearly: parseFloat(form.add_user_price_yearly) || 0,
                base_maps_calls_price: parseFloat(form.base_maps_calls_price) || 0,
                base_ai_credits_price: parseFloat(form.base_ai_credits_price) || 0,
            });

            if (response.data.response_code === 1) {

                const createdPlan = response.data.data;

                navigate(`/our-plans/detail/${createdPlan.id}`);

                SuccessAlert("Plan created successfully");
            } else {
                // message is an object of field errors OR a plain string
                const msg = response.data.message;
                if (msg && typeof msg === 'object') {
                    setErrors(msg);          // { plan_name: [...], currency: [...], ... }
                } else {
                    setErrors({ general: msg || "Something went wrong" });
                }
            }
        } catch (error) {
            console.log(error);
            if (error.response?.data) {
                const data = error.response.data;
                const msg = data.message;
                if (msg && typeof msg === 'object') {
                    setErrors(msg);
                } else if (msg) {
                    setErrors({ general: msg });
                } else {
                    setErrors({ general: "Something went wrong" });
                }
            } else {
                setErrors({ general: "Network error" });
            }
        }
    };


    return (
        <div className='whole-container'>
            <div className='top-heading'>
                <PageHeader pageName="Plans / Our Plans /" nestedpageName="Create" linkTo="/our-plans" />
                <div className='export-container'>
                    <ExportLabel />
                </div>
            </div>
            <div className="page-container">
                <ErrorNotification
                    errors={errors}
                    marginBottom="16px"
                />
                {/* ── Plan Details Card ── */}
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">Plan Details</span>
                        <button className="popup-add-button common-text white fts-15 ftw-5" onClick={handleSubmit} >Add Plan</button>
                    </div>

                    <div className="card-body">

                        <InputBoxLarge
                            label="Plan Name"
                            defaultValue={form.plan_name}
                            onChange={(e) => handleChange('plan_name', e.target.value)}
                            isRequired
                        />

                        <InputBoxLarge
                            label="Display Name"
                            defaultValue={form.display_name}
                            onChange={(e) => handleChange('display_name', e.target.value)}
                            isRequired
                        />

                        <div className="row-2">
                            <InputAndSelectCompoBoxSmall
                                label="Price Monthly"
                                isRequired={true}
                                options={currencies}   // from API
                                defaultValue={form.monthly_price}
                                defaultOption={form.currency}
                                numericType="decimal"
                                onChange={(e) => handleChange('monthly_price', e.target.value.replace(/\\D/g, ''))}
                                selectchange={(e) => handleChange('currency', e.target.value)}
                            />

                            <InputAndSelectCompoBoxSmall
                                label="Price Yearly"
                                isRequired={true}
                                options={currencies}
                                defaultValue={form.yearly_price}
                                defaultOption={form.currency}
                                numericType="decimal"
                                onChange={(e) => handleChange('yearly_price', e.target.value.replace(/\\D/g, ''))}
                                selectchange={(e) => handleChange('currency', e.target.value)}
                            />
                        </div>

                        <InputBoxLarge
                            label="Badge Text"
                            defaultValue={form.badge_text}
                            onChange={(e) => handleChange('badge_text', e.target.value)}
                        />

                        <div className="row-2">
                            <InputBoxSmall
                                label="Default User Limit"
                                isRequired={true}
                                defaultValue={form.default_users}
                                numericType="integer"
                                onChange={(e) => handleChange('default_users', e.target.value.replace(/\\D/g, ''))}
                            />
                            <InputBoxSmall
                                label="Monthly Maps Calls"
                                defaultValue={form.default_maps_calls_monthly}
                                numericType="integer"
                                onChange={(e) => handleChange('default_maps_calls_monthly', e.target.value.replace(/\\D/g, ''))}
                            />
                            <InputBoxSmall
                                label="Yearly Maps Calls"
                                defaultValue={form.default_maps_calls_yearly}
                                numericType="integer"
                                onChange={(e) => handleChange('default_maps_calls_yearly', e.target.value.replace(/\\D/g, ''))}
                            />
                        </div>

                        <div className="row-2">
                            <InputBoxSmall
                                label="Monthly AI Credits"
                                defaultValue={form.default_ai_credits_monthly}
                                numericType="integer"
                                onChange={(e) => handleChange('default_ai_credits_monthly', e.target.value.replace(/\\D/g, ''))}
                            />
                            <InputBoxSmall
                                label="Yearly AI Credits"
                                defaultValue={form.default_ai_credits_yearly}
                                numericType="integer"
                                onChange={(e) => handleChange('default_ai_credits_yearly', e.target.value.replace(/\\D/g, ''))}
                            />
                        </div>

                        <div className="row-2">
                            <InputAndSelectCompoBoxSmall
                                label="Monthly User Price"
                                options={currencies}
                                defaultValue={form.add_user_price_monthly}
                                defaultOption={form.currency}
                                numericType="decimal"
                                onChange={(e) => handleChange('add_user_price_monthly', e.target.value.replace(/\\D/g, ''))}
                                selectchange={(e) => handleChange('currency', e.target.value)}
                            />
                            <InputAndSelectCompoBoxSmall
                                label="Yearly User Price"
                                options={currencies}
                                defaultValue={form.add_user_price_yearly}
                                defaultOption={form.currency}
                                numericType="decimal"
                                onChange={(e) => handleChange('add_user_price_yearly', e.target.value.replace(/\\D/g, ''))}
                                selectchange={(e) => handleChange('currency', e.target.value)}
                            />
                        </div>

                        <div className="row-2">
                            <InputBoxSmall
                                label="Maps Calls Base Limit"
                                defaultValue={form.base_maps_calls_count}
                                numericType="integer"
                                onChange={(e) => handleChange('base_maps_calls_count', e.target.value.replace(/\\D/g, ''))}
                            />
                            <InputAndSelectCompoBoxSmall
                                label="Maps Calls Base Price"
                                options={currencies}
                                defaultValue={form.base_maps_calls_price}
                                defaultOption={form.currency}
                                numericType="decimal"
                                onChange={(e) => handleChange('base_maps_calls_price', e.target.value.replace(/\\D/g, ''))}
                                selectchange={(e) => handleChange('currency', e.target.value)}
                            />
                        </div>

                        <div className="row-2   ">
                            <InputBoxSmall
                                label="AI Credits Base Limit"
                                defaultValue={form.base_ai_credits_count}
                                numericType="integer"
                                onChange={(e) => handleChange('base_ai_credits_count', e.target.value.replace(/\\D/g, ''))}
                            />
                            <InputAndSelectCompoBoxSmall
                                label="AI Credits Base Price"
                                options={currencies}
                                defaultValue={form.base_ai_credits_price}
                                defaultOption={form.currency}
                                numericType="decimal"
                                onChange={(e) => handleChange('base_ai_credits_price', e.target.value.replace(/\\D/g, ''))}
                                selectchange={(e) => handleChange('currency', e.target.value)}
                            />
                        </div>

                        <div className="toggle-group">
                            <div className="toggle-item">
                                <span className='toggle-label'>Is Free Plan</span>
                                <div className='plan-toggle-switch-container'>
                                <ToggleSwitch
                                    defaultValue={form.is_freeplan}
                                    onToggle={(value) => handleChange('is_freeplan', value)}
                                />
                                </div>
                            </div>

                            <div className="toggle-item">
                                <span className='toggle-label'>Advanced Plan</span>
                                <div className='plan-toggle-switch-container'>
                                <ToggleSwitch
                                    defaultValue={form.is_advanced}
                                    onToggle={(value) => handleChange('is_advanced', value)}
                                />
                                </div>
                            </div>
                        </div>

                        {form.is_freeplan && (
                            <InputBoxSmall
                                label="Trial Period / Days"
                                defaultValue={form.trial_period}
                                numericType="integer"
                                onChange={(e) => handleChange('trial_period', e.target.value)}
                            />
                        )}

                    </div>
                </div>

                {/* ── Add Plan Features Card ── */}
                <div className="features-card">
                    <FeaturesList
                        title="Add Plan Features"
                        isRequired
                        allFeatures={features}
                        defaultSelectedOptions={form.features}
                        onChange={(selected) => handleChange('features', selected)}
                    />
                </div>
            </div>
        </div>
    );
};

export default OurPlansAdd;